import { Check, CircleDashed, Globe2, RotateCcw, Shapes, X } from "lucide-react";

import { DistrictPicker } from "@/districts/DistrictPicker.tsx";
import { Hint } from "@/design-system/Hint.tsx";
import { RadarMark } from "@/app/RadarMark.tsx";
import { ThemeToggle } from "@/theme/ThemeToggle.tsx";
import type { ResolvedTheme, ThemeMode } from "@/theme/theme.ts";
import { CheckBox } from "@/design-system/CheckBox.tsx";
import { StopSlider } from "@/design-system/StopSlider.tsx";
import { Switch } from "@/design-system/primitives/switch";
import { AGE_SCALE } from "@/listings/listing.freshness.ts";
import type { FilterResult } from "@/listings/listing.filter.ts";
import { agoLabel, distanceLabel, plural } from "@/listings/listing.format.ts";
import type { IndexedDistrict } from "@/districts/district.model.ts";
import {
  AGE_STOPS,
  ageStopLabel,
  isFiltered,
  PRICE_ANY,
  PRICE_NO_FLOOR,
  PRICE_STOPS,
  priceStopLabel,
  SQFT_NONE,
  SQFT_STOPS,
  sqftStopLabel,
  TRAVEL_ANY,
  TRAVEL_MODE_LABELS,
  TRAVEL_STOPS,
  travelStopLabel,
  type AppState,
  type TravelFilterMode,
} from "@/search/search.state.ts";
import { BED_BUCKETS, BED_LABELS, type BedBucket } from "@/listings/listing.filter.ts";
import { type DistrictSet } from "@/districts/district.model.ts";
import { cn } from "@/design-system/cn.ts";
import type { DraftCircle } from "@/map/MapCanvas.tsx";

export type AreaTool = "none" | "radius" | "districts";

export interface ConsolePanelProps {
  state: AppState;
  patch: (next: Partial<AppState>) => void;
  /** patch derived from state at apply time — required for anything that toggles. */
  update: (fn: (s: AppState) => Partial<AppState>) => void;
  result: FilterResult;
  lastIngestAt: number | null;
  loading: boolean;

  tool: AreaTool;
  onToolChange: (tool: AreaTool) => void;
  draftCircle: DraftCircle | null;
  onDraftCircleChange: (c: DraftCircle | null) => void;
  draftDistrictIds: string[];
  onDraftDistrictsChange: (ids: string[]) => void;
  /** Toggle one district in the draft selection (functional, so rapid taps stack). */
  onToggleDistrict: (id: string) => void;
  onApplyArea: () => void;
  onClearArea: () => void;
  onResetAll: () => void;
  onFocusArea: () => void;

  districts: IndexedDistrict[];
  districtCounts: Map<string, number>;

  /** How many listings in the current data this browser has hidden. */
  hiddenCount: number;

  themeMode: ThemeMode;
  themeResolved: ResolvedTheme;
  onCycleTheme: () => void;

  /** Mobile only — the drawer's own close affordance. */
  onCloseDrawer?: () => void;
}

export function ConsolePanel(p: ConsolePanelProps) {
  const { state, patch, update, result } = p;
  // Clamped at the top, not the bottom, unlike the two below: revealing a shared
  // listing older than the slider can ask for widens the filter past its last
  // stop on purpose, and the thumb should sit at that end rather than snap to
  // the other one. See `onRevealShared`.
  const ageStop = AGE_STOPS.findIndex((s) => s >= state.maxAgeDays);
  const ageIndex = ageStop === -1 ? AGE_STOPS.length - 1 : ageStop;

  const priceIndex = Math.max(
    0,
    PRICE_STOPS.findIndex((p2) => p2 >= state.maxPrice),
  );

  // Kept strictly below the cap's stop, mirroring the slider's own
  // minStepsBetweenThumbs — a saved blob can't render the handles crossed.
  const minPriceIndex = Math.min(
    Math.max(
      0,
      PRICE_STOPS.findIndex((p2) => p2 >= state.minPrice),
    ),
    priceIndex - 1,
  );

  const travelIndex = Math.max(
    0,
    TRAVEL_STOPS.findIndex((t) => t >= state.maxTravelMin),
  );

  const sqftIndex = Math.max(
    0,
    SQFT_STOPS.findIndex((s) => s >= state.minSqft),
  );

  const toggleBed = (bucket: BedBucket) => {
    update((s) => ({
      beds: s.beds.includes(bucket) ? s.beds.filter((b) => b !== bucket) : [...s.beds, bucket],
    }));
  };

  return (
    // `flex-1 min-h-0` (not `h-full`): the desktop shell is a max-height flex
    // column with an auto height, where a percentage height would resolve to
    // auto and let the footer slide off the bottom of the viewport.
    <div className="flex min-h-0 flex-1 flex-col">
      <Header {...p} />

      {/* `touch-pan-y`, not the default `auto`: it hands vertical scrolling to
          the browser and keeps horizontal for the drawer's swipe-to-close. Left
          at `auto` the browser is free to read a sideways drag as a pan of its
          own, and it takes the pointer stream with it — which is why swiping
          closed from the middle of the panel stopped working. */}
      <div className="touch-scrollbar-none min-h-0 flex-1 touch-pan-y overflow-y-auto overscroll-contain">
        {/* ── 01 freshness ──────────────────────────────────────────────── */}
        <Section index="01" title="Freshness">
          <div className="flex items-end justify-between">
            <div className="flex items-baseline gap-1.5">
              <span className="tnum font-mono text-[26px] font-light leading-none tracking-tighter text-chalk">
                {ageStopLabel(state.maxAgeDays).split(" ")[0]}
              </span>
              <span className="eyebrow pb-0.5">
                {ageStopLabel(state.maxAgeDays).split(" ")[1] ?? ""}
              </span>
            </div>
            <span className="eyebrow pb-1">or newer</span>
          </div>

          <StopSlider
            className="mt-3"
            value={ageIndex}
            max={AGE_STOPS.length - 1}
            stops={AGE_STOPS.length}
            onChange={(i) => patch({ maxAgeDays: AGE_STOPS[i]! })}
            label="Maximum listing age"
          />

          <Legend />

          {result.droppedByAge > 0 && (
            <p className="mt-2 font-mono text-[9.5px] uppercase tracking-[0.1em] text-chalk-mute">
              {result.droppedByAge.toLocaleString()} older hidden
            </p>
          )}
        </Section>

        {/* ── 02 budget ─────────────────────────────────────────────────── */}
        <Section index="02" title="Budget">
          {(() => {
            // One readout, three shapes: "$4.5 k / or less" with no floor,
            // "$2–4.5 k / range" with both ends, "$2 k / or more" with only a
            // floor. Every stop above zero labels as "$X k", which is what lets
            // the range form share one unit token.
            const floored = state.minPrice > PRICE_NO_FLOOR;
            const capped = state.maxPrice < PRICE_ANY;
            const [maxBig, maxUnit] = priceStopLabel(state.maxPrice).split(" ");
            const [minBig, minUnit] = priceStopLabel(state.minPrice).split(" ");
            const readout = !floored
              ? { big: maxBig, unit: maxUnit, side: "or less" }
              : capped
                ? { big: `${minBig}–${maxBig!.slice(1)}`, unit: maxUnit, side: "range" }
                : { big: minBig, unit: minUnit, side: "or more" };
            return (
              <div className="flex items-end justify-between">
                <div className="flex items-baseline gap-1.5">
                  <span className="tnum font-mono text-[26px] leading-none font-light tracking-tighter text-chalk">
                    {readout.big}
                  </span>
                  <span className="eyebrow pb-0.5">{readout.unit ?? ""}</span>
                </div>
                <span className="eyebrow pb-1">{readout.side}</span>
              </div>
            );
          })()}

          <StopSlider
            className="mt-3"
            low={minPriceIndex}
            value={priceIndex}
            max={PRICE_STOPS.length - 1}
            stops={PRICE_STOPS.length}
            onChange={(i) => patch({ maxPrice: PRICE_STOPS[i]! })}
            onLowChange={(i) => patch({ minPrice: PRICE_STOPS[i]! })}
            label="Monthly rent range"
            valueText={priceRangeText(state.minPrice, state.maxPrice)}
          />

          {result.droppedByPrice > 0 && (
            <p className="mt-2 font-mono text-[9.5px] tracking-[0.1em] text-chalk-mute uppercase">
              {result.droppedByPrice.toLocaleString()} outside budget hidden
            </p>
          )}
        </Section>

        {/* ── 03 size ───────────────────────────────────────────────────── */}
        <Section index="03" title="Size">
          <div className="flex items-end justify-between">
            <div className="flex items-baseline gap-1.5">
              <span className="tnum font-mono text-[26px] leading-none font-light tracking-tighter text-chalk">
                {sqftStopLabel(state.minSqft).split(" ")[0]}
              </span>
              <span className="eyebrow pb-0.5">
                {sqftStopLabel(state.minSqft).split(" ")[1] ?? ""}
              </span>
            </div>
            {/* The one slider that reads the other way: a *minimum*, because
                nobody hides an apartment for being too big. */}
            <span className="eyebrow pb-1">or more</span>
          </div>

          <StopSlider
            className="mt-3"
            value={sqftIndex}
            max={SQFT_STOPS.length - 1}
            stops={SQFT_STOPS.length}
            onChange={(i) => patch({ minSqft: SQFT_STOPS[i]! })}
            label="Minimum square footage"
            valueText={
              state.minSqft <= SQFT_NONE
                ? "No minimum"
                : `${state.minSqft.toLocaleString("en-US")} square feet or more`
            }
          />

          {/* Inert at ANY, where no floor means nothing is being judged by its
              size — really `disabled`, for the same keyboard reason as the
              travel modes below. */}
          <label
            className={cn(
              "mt-2 flex cursor-pointer items-center gap-2 text-[11.5px] text-chalk-mute transition hover:text-chalk-dim",
              state.minSqft <= SQFT_NONE && "cursor-default opacity-40 hover:text-chalk-mute",
            )}
          >
            <CheckBox
              checked={state.includeUnknownSqft}
              disabled={state.minSqft <= SQFT_NONE}
              onCheckedChange={(v) => patch({ includeUnknownSqft: v === true })}
            />
            Include listings with no listed sqft
          </label>

          {/* Same disjoint pair as travel: "too small" is the filter working,
              "unsized" is a data gap worth naming before trusting the result. */}
          {result.droppedBySqft > 0 &&
            (() => {
              const tooSmall = result.droppedBySqft - result.droppedBySqftUnknown;
              return (
                <p className="mt-2 font-mono text-[9.5px] tracking-[0.1em] text-chalk-mute uppercase">
                  {tooSmall > 0 && `${tooSmall.toLocaleString()} smaller hidden`}
                  {tooSmall > 0 && result.droppedBySqftUnknown > 0 && " · "}
                  {result.droppedBySqftUnknown > 0 &&
                    `${result.droppedBySqftUnknown.toLocaleString()} unsized`}
                </p>
              );
            })()}
        </Section>

        {/* ── 04 travel ─────────────────────────────────────────────────── */}
        <Section index="04" title="Travel">
          <div className="flex items-end justify-between">
            <div className="flex items-baseline gap-1.5">
              <span className="tnum font-mono text-[26px] leading-none font-light tracking-tighter text-chalk">
                {travelStopLabel(state.maxTravelMin).split(" ")[0]}
              </span>
              <span className="eyebrow pb-0.5">
                {travelStopLabel(state.maxTravelMin).split(" ")[1] ?? ""}
              </span>
            </div>
            <span className="eyebrow pb-1">or less</span>
          </div>

          <StopSlider
            className="mt-3"
            value={travelIndex}
            max={TRAVEL_STOPS.length - 1}
            stops={TRAVEL_STOPS.length}
            onChange={(i) => patch({ maxTravelMin: TRAVEL_STOPS[i]! })}
            label="Maximum travel time to the anchor"
            valueText={
              state.maxTravelMin >= TRAVEL_ANY
                ? "No limit"
                : `${state.maxTravelMin} minutes or less by ${TRAVEL_MODE_LABELS[state.travelMode]}`
            }
          />

          {/* Which mode the limit is measured by — inert at ANY, where there is
              no limit for a mode to qualify. Really `disabled`, not just
              pointer-events-none: that only stops the mouse, and a keyboard user
              would still tab in and silently change a setting with no effect. */}
          <div
            className={cn(
              "mt-3 grid grid-cols-4 gap-1.5 transition-opacity",
              state.maxTravelMin >= TRAVEL_ANY && "opacity-40",
            )}
          >
            {(Object.keys(TRAVEL_MODE_LABELS) as TravelFilterMode[]).map((mode) => {
              const active = state.travelMode === mode;
              return (
                <button
                  key={mode}
                  type="button"
                  disabled={state.maxTravelMin >= TRAVEL_ANY}
                  onClick={() => patch({ travelMode: mode })}
                  aria-pressed={active}
                  className={cn(
                    "rounded-lg border px-1 py-1.5 font-mono text-[9.5px] font-medium tracking-[0.06em] uppercase transition disabled:cursor-not-allowed",
                    active
                      ? "border-signal-ink/50 bg-signal/12 text-chalk"
                      : "border-rule bg-ink-900/40 text-chalk-mute enabled:hover:border-rule-bright enabled:hover:text-chalk-dim",
                  )}
                >
                  {TRAVEL_MODE_LABELS[mode]}
                </button>
              );
            })}
          </div>

          {/* The two counts are disjoint here, where they used to nest: untimed
              listings were previously counted in the total and then again as
              "of them", which read as more hidden than there were.

              `untimed` stays reported even though the ingest now times almost
              everything. It costs a line only when it is non-zero, and it is the
              one number that tells the difference between "too far" and "never
              measured" if coverage ever slips again. */}
          {result.droppedByTravel > 0 &&
            (() => {
              const timedOut = result.droppedByTravel - result.droppedByTravelUnknown;
              return (
                <p className="mt-2 font-mono text-[9.5px] tracking-[0.1em] text-chalk-mute uppercase">
                  {timedOut > 0 && `${timedOut.toLocaleString()} hidden`}
                  {timedOut > 0 && result.droppedByTravelUnknown > 0 && " · "}
                  {result.droppedByTravelUnknown > 0 &&
                    `${result.droppedByTravelUnknown.toLocaleString()} untimed`}
                </p>
              );
            })()}
        </Section>

        {/* ── 05 layout ─────────────────────────────────────────────────── */}
        <Section index="05" title="Layout">
          <div className="grid grid-cols-4 gap-1.5">
            {BED_BUCKETS.map((bucket) => {
              const checked = state.beds.includes(bucket);
              return (
                <label
                  key={bucket}
                  className={cn(
                    "group flex cursor-pointer select-none flex-col items-center gap-1.5 rounded-lg border px-1 py-2 transition",
                    checked
                      ? "border-signal-ink/50 bg-signal/12 text-chalk"
                      : "border-rule bg-ink-900/40 text-chalk-mute hover:border-rule-bright hover:text-chalk-dim",
                  )}
                >
                  <CheckBox
                    checked={checked}
                    onCheckedChange={() => toggleBed(bucket)}
                    aria-label={BED_LABELS[bucket]}
                  />
                  <span className="font-mono text-[9.5px] font-medium uppercase tracking-[0.06em]">
                    {BED_LABELS[bucket]}
                  </span>
                </label>
              );
            })}
          </div>

          <label className="mt-2 flex cursor-pointer items-center gap-2 text-[11.5px] text-chalk-mute transition hover:text-chalk-dim">
            <CheckBox
              checked={state.includeUnknownBeds}
              onCheckedChange={(v) => patch({ includeUnknownBeds: v === true })}
            />
            Include listings with no bed count
          </label>
        </Section>

        {/* ── 06 area ───────────────────────────────────────────────────── */}
        <Section index="06" title="Area">
          <AreaSection {...p} />
        </Section>
      </div>

      <Footer {...p} />
    </div>
  );
}

// ── header ──────────────────────────────────────────────────────────────────

function Header(p: ConsolePanelProps) {
  const { lastIngestAt, loading, onCloseDrawer } = p;
  return (
    <header className="flex items-center gap-3 border-b border-rule px-4 py-3.5">
      <RadarMark className="size-8 shrink-0" />
      <div className="min-w-0 flex-1">
        <h1 className="font-mono text-[15px] leading-none font-medium tracking-[0.2em] text-chalk">
          RADAR
        </h1>
        <Hint
          label={loading ? "Fetching listings" : "Last ingest"}
          detail={
            lastIngestAt
              ? `The hourly job last wrote to the shared database ${agoLabel(lastIngestAt)}.`
              : "Waiting for the first response from the listings API."
          }
          side="bottom"
          align="start"
        >
          <button
            type="button"
            className="mt-1 flex cursor-default items-center gap-1.5 font-mono text-[8.5px] tracking-[0.14em] text-chalk-mute uppercase"
          >
            <span
              className={cn(
                "relative inline-block size-1.5 rounded-full",
                loading ? "bg-chalk-mute" : "pulse bg-signal",
              )}
            />
            {loading ? "syncing" : lastIngestAt ? `ingest ${agoLabel(lastIngestAt)}` : "sf rentals"}
          </button>
        </Hint>
      </div>

      <ThemeToggle mode={p.themeMode} resolved={p.themeResolved} onCycle={p.onCycleTheme} />

      {onCloseDrawer && (
        <Hint label="Close controls" detail="Or swipe the panel left." side="bottom">
          <button
            type="button"
            onClick={onCloseDrawer}
            aria-label="Close controls"
            className="grid size-8 shrink-0 place-items-center rounded-lg border border-rule text-chalk-dim transition hover:border-rule-bright hover:text-chalk active:scale-95"
          >
            <X className="size-4" />
          </button>
        </Hint>
      )}
    </header>
  );
}

function Section({
  index,
  title,
  children,
}: {
  index: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="border-b border-rule px-4 py-3.5">
      <div className="rule-tick mb-3 flex items-center gap-2 pb-1.5">
        <span className="font-mono text-[9px] font-medium tracking-[0.1em] text-signal-ink/80">
          {index}
        </span>
        <h2 className="eyebrow text-chalk-dim">{title}</h2>
      </div>
      {children}
    </section>
  );
}

// ── area ────────────────────────────────────────────────────────────────────

const TOOLS: { id: AreaTool; label: string; icon: typeof Globe2 }[] = [
  { id: "none", label: "All", icon: Globe2 },
  { id: "radius", label: "Radius", icon: CircleDashed },
  { id: "districts", label: "Districts", icon: Shapes },
];

function AreaSection(p: ConsolePanelProps) {
  const { state, tool } = p;
  const applied = state.area;

  return (
    <div className="space-y-3">
      {/* The three modes are mutually exclusive by construction: one segmented
          control, one active area in state. */}
      <div
        role="radiogroup"
        aria-label="Area filter mode"
        className="grid grid-cols-3 gap-1 rounded-lg border border-rule bg-ink-900/60 p-0.5"
      >
        {TOOLS.map((t) => {
          const active =
            tool === t.id ||
            (tool === "none" &&
              ((t.id === "radius" && applied.mode === "radius") ||
                (t.id === "districts" && applied.mode === "districts") ||
                (t.id === "none" && applied.mode === "all")));
          return (
            <button
              key={t.id}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => p.onToolChange(t.id)}
              className={cn(
                "flex items-center justify-center gap-1.5 rounded-[6px] px-1.5 py-2 font-mono text-[9px] font-medium uppercase tracking-[0.1em] transition",
                active
                  ? "bg-marine-ink/12 text-marine-ink shadow-[inset_0_0_0_1px_rgb(58_220_200_/_0.3)]"
                  : "text-chalk-mute hover:text-chalk-dim",
              )}
            >
              <t.icon className="size-3" />
              {t.label}
            </button>
          );
        })}
      </div>

      {tool === "radius" && <RadiusEditor {...p} />}
      {tool === "districts" && <DistrictEditor {...p} />}
      {tool === "none" && applied.mode !== "all" && <AppliedArea {...p} />}
      {tool === "none" && applied.mode === "all" && (
        <p className="text-[11.5px] leading-relaxed text-chalk-mute">
          Showing the whole Bay Area. Draw a radius or pick districts to narrow it down.
        </p>
      )}
    </div>
  );
}

function RadiusEditor(p: ConsolePanelProps) {
  const c = p.draftCircle;
  return (
    <div className="space-y-3">
      {!c ? (
        <p className="rounded-lg border border-dashed border-marine-ink/45 bg-marine-ink/6 px-3 py-3 text-center text-[11.5px] leading-relaxed text-marine-ink/90">
          Tap the map to drop a centre point.
        </p>
      ) : (
        <>
          <div className="flex items-baseline justify-between">
            <span className="eyebrow">radius</span>
            <span className="tnum font-mono text-[15px] text-marine-ink">
              {distanceLabel(c.radiusM)}
            </span>
          </div>
          <StopSlider
            value={Math.round(c.radiusM)}
            min={150}
            max={12000}
            step={50}
            onChange={(radiusM) => p.onDraftCircleChange({ ...c, radiusM })}
            label="Radius in metres"
            accent="marine"
          />
          <p className="text-[11px] leading-relaxed text-chalk-mute">
            Drag the centre dot to move it, or the edge handle to resize.
          </p>
        </>
      )}
      <ApplyRow
        canApply={c !== null}
        applyLabel="Apply radius"
        onApply={p.onApplyArea}
        onCancel={() => p.onToolChange("none")}
      />
    </div>
  );
}

function DistrictEditor(p: ConsolePanelProps) {
  return (
    <div className="space-y-3">
      <DistrictPicker
        districts={p.districts}
        set={p.state.districtSet}
        onSetChange={(set: DistrictSet) => {
          // Ids are namespaced per set, so switching sets starts a clean selection.
          p.patch({ districtSet: set });
          p.onDraftDistrictsChange([]);
        }}
        selected={p.draftDistrictIds}
        onToggle={p.onToggleDistrict}
        onSelectNone={() => p.onDraftDistrictsChange([])}
        counts={p.districtCounts}
      />
      <ApplyRow
        canApply={p.draftDistrictIds.length > 0}
        applyLabel={`Apply ${p.draftDistrictIds.length || ""}`.trim()}
        onApply={p.onApplyArea}
        onCancel={() => p.onToolChange("none")}
      />
    </div>
  );
}

function ApplyRow({
  canApply,
  applyLabel,
  onApply,
  onCancel,
}: {
  canApply: boolean;
  applyLabel: string;
  onApply: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="flex gap-1.5">
      <button
        type="button"
        onClick={onCancel}
        className="rounded-lg border border-rule px-3 py-2 font-mono text-[9.5px] font-medium uppercase tracking-[0.1em] text-chalk-mute transition hover:border-rule-bright hover:text-chalk"
      >
        Cancel
      </button>
      <button
        type="button"
        disabled={!canApply}
        onClick={onApply}
        className="flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-marine px-3 py-2 font-mono text-[9.5px] font-semibold uppercase tracking-[0.1em] text-on-accent transition hover:bg-marine-deep disabled:cursor-not-allowed disabled:bg-ink-600 disabled:text-chalk-mute"
      >
        <Check className="size-3" />
        {applyLabel}
      </button>
    </div>
  );
}

/** The chip shown once an area is applied and its geometry has left the map. */
function AppliedArea(p: ConsolePanelProps) {
  const area = p.state.area;
  const label =
    area.mode === "radius"
      ? `${distanceLabel(area.radiusM)} radius`
      : area.mode === "districts"
        ? plural(
            area.ids.length,
            p.state.districtSet === "supervisor" ? "district" : "neighbourhood",
          )
        : "";

  const detail =
    area.mode === "districts"
      ? area.ids
          .map((id) => p.districts.find((d) => d.id === id)?.name)
          .filter(Boolean)
          .join(" · ")
      : null;

  return (
    <div className="rounded-lg border border-marine-ink/35 bg-marine-ink/10 p-2.5">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-mono text-[10px] font-medium uppercase tracking-[0.1em] text-marine-ink">
            {label}
          </p>
          {detail && (
            <p className="mt-1 line-clamp-2 text-[11px] leading-snug text-chalk-dim">{detail}</p>
          )}
          <p className="mt-1 font-mono text-[9px] uppercase tracking-[0.1em] text-chalk-mute">
            {result(p)} inside
          </p>
        </div>
        <button
          type="button"
          onClick={p.onClearArea}
          aria-label="Clear area filter"
          className="grid size-6 shrink-0 place-items-center rounded-md border border-marine-ink/30 text-marine-ink/80 transition hover:border-marine-ink hover:text-marine-ink"
        >
          <X className="size-3" />
        </button>
      </div>
      <div className="mt-2 flex gap-1.5">
        <button
          type="button"
          onClick={p.onFocusArea}
          className="flex-1 rounded-md border border-marine-ink/30 px-2 py-1.5 font-mono text-[9px] uppercase tracking-[0.1em] text-marine-ink/90 transition hover:border-marine-ink hover:text-marine-ink"
        >
          Zoom to area
        </button>
        <button
          type="button"
          onClick={() => p.onToolChange(area.mode === "radius" ? "radius" : "districts")}
          className="flex-1 rounded-md border border-marine-ink/30 px-2 py-1.5 font-mono text-[9px] uppercase tracking-[0.1em] text-marine-ink/90 transition hover:border-marine-ink hover:text-marine-ink"
        >
          Edit
        </button>
      </div>
    </div>
  );
}

const result = (p: ConsolePanelProps): string => plural(p.result.visible.length, "listing");

const usd = (n: number) => `$${n.toLocaleString("en-US")}`;

/** What a screen reader hears for the budget range slider. */
function priceRangeText(minPrice: number, maxPrice: number): string {
  if (minPrice <= PRICE_NO_FLOOR) {
    return maxPrice >= PRICE_ANY ? "No limit" : `${usd(maxPrice)} per month or less`;
  }
  return maxPrice >= PRICE_ANY
    ? `${usd(minPrice)} per month or more`
    : `${usd(minPrice)} to ${usd(maxPrice)} per month`;
}

// ── footer ──────────────────────────────────────────────────────────────────

function Footer(p: ConsolePanelProps) {
  // Nothing to reset to — don't offer an action that would visibly do nothing.
  const dirty = isFiltered(p.state);
  return (
    <footer className="space-y-2.5 border-t border-rule bg-ink-950/40 px-4 py-3">
      {/* A display preference rather than a filter, so it sits outside the
          numbered sections — it changes how the same set is drawn, not which
          listings are in it. */}
      <Hint
        label="Group nearby listings"
        detail="Off, every listing gets its own dot at any zoom — denser, but nothing is hidden inside a bubble."
        side="top"
      >
        <label className="flex cursor-pointer items-center justify-between gap-3 rounded-lg border border-rule bg-ink-900/40 px-2.5 py-2 transition hover:border-rule-bright">
          <span className="font-mono text-[9.5px] font-medium tracking-[0.1em] text-chalk-dim uppercase">
            Group nearby
          </span>
          <Switch
            checked={p.state.clusterListings}
            onCheckedChange={(v) => p.patch({ clusterListings: v })}
            aria-label="Group nearby listings into clusters"
            /* The thumb's travel has to be restated alongside its size. The
               primitive moves it by calc(100% - 2px), which is correct only for
               the 16px thumb it ships with; at size-3.5 that becomes 12px while
               the track's 28px inner width still needs 14px, so it parks 2px
               short of the right edge and reads as not-quite-on. The distance is
               30px of inner track (w-8 less the 1px borders) minus the 14px
               thumb, so 16px — not the 14 I first guessed off the outer width. */
            className="h-[18px] w-8 data-[state=checked]:bg-signal-ink data-[state=unchecked]:bg-ink-600 [&_[data-slot=switch-thumb]]:size-3.5 [&_[data-slot=switch-thumb]]:bg-ink-900 [&_[data-slot=switch-thumb][data-state=checked]]:translate-x-4"
          />
        </label>
      </Hint>

      <Hint
        label="Bike infrastructure"
        detail="Streets that are survivable on two wheels — solid green is physically separated from traffic, dashed is a painted lane on the road."
        side="top"
      >
        <label className="flex cursor-pointer items-center justify-between gap-3 rounded-lg border border-rule bg-ink-900/40 px-2.5 py-2 transition hover:border-rule-bright">
          <span className="font-mono text-[9.5px] font-medium tracking-[0.1em] text-chalk-dim uppercase">
            Bike lanes
          </span>
          <Switch
            checked={p.state.showBikeways}
            onCheckedChange={(v) => p.patch({ showBikeways: v })}
            aria-label="Show bike lanes and paths"
            /* Same thumb-travel restatement as the switch above. */
            className="h-[18px] w-8 data-[state=checked]:bg-signal-ink data-[state=unchecked]:bg-ink-600 [&_[data-slot=switch-thumb]]:size-3.5 [&_[data-slot=switch-thumb]]:bg-ink-900 [&_[data-slot=switch-thumb][data-state=checked]]:translate-x-4"
          />
        </label>
      </Hint>

      {/* Only rendered once there is something to show (or the switch is
          already on, so it can always be turned back off) — a control for an
          empty set would just be a switch that visibly does nothing. */}
      {(p.hiddenCount > 0 || p.state.showHidden) && (
        <Hint
          label="Hidden listings"
          detail={`${plural(p.hiddenCount, "listing")} in the current data ${p.hiddenCount === 1 ? "is" : "are"} hidden. On, they return to the map; open one and click the eye to restore it for good.`}
          side="top"
        >
          <label className="flex cursor-pointer items-center justify-between gap-3 rounded-lg border border-rule bg-ink-900/40 px-2.5 py-2 transition hover:border-rule-bright">
            <span className="font-mono text-[9.5px] font-medium tracking-[0.1em] text-chalk-dim uppercase">
              Show hidden{p.hiddenCount > 0 ? ` · ${p.hiddenCount.toLocaleString()}` : ""}
            </span>
            <Switch
              checked={p.state.showHidden}
              onCheckedChange={(v) => p.patch({ showHidden: v })}
              aria-label="Show hidden listings"
              /* Same thumb-travel restatement as the switches above. */
              className="h-[18px] w-8 data-[state=checked]:bg-signal-ink data-[state=unchecked]:bg-ink-600 [&_[data-slot=switch-thumb]]:size-3.5 [&_[data-slot=switch-thumb]]:bg-ink-900 [&_[data-slot=switch-thumb][data-state=checked]]:translate-x-4"
            />
          </label>
        </Hint>
      )}

      <Hint
        label="Reset filters"
        detail="Back to the defaults. Your position on the map and your theme are kept."
        side="top"
      >
        <button
          type="button"
          onClick={p.onResetAll}
          disabled={!dirty}
          className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-rule px-3 py-2 font-mono text-[9.5px] font-medium tracking-[0.1em] text-chalk-dim uppercase transition hover:border-rule-bright hover:text-chalk active:scale-[0.99] disabled:pointer-events-none disabled:opacity-35"
        >
          <RotateCcw className="size-3" />
          Reset filters
        </button>
      </Hint>
    </footer>
  );
}

/** The age ramp, spelled out. Without it the map's colour is decoration — so it
 *  sits under the control that sets the range, not stranded in a footer. */
function Legend() {
  return (
    <div className="mt-3">
      {/* Only the first stop breathes, and its label breathes with it. That is
          the whole point: it names what a flashing mark on the map means, and
          it can only do that if the rest of the ramp stays still. */}
      <div className="flex h-1.5 overflow-hidden rounded-full">
        {AGE_SCALE.map((s, i) => (
          <span
            key={s.label}
            className={i === 0 ? "flex-1 animate-pulse motion-reduce:animate-none" : "flex-1"}
            style={{ background: s.color }}
          />
        ))}
      </div>
      <div className="mt-1 flex justify-between font-mono text-[8px] uppercase tracking-[0.08em] text-chalk-mute">
        <span className="animate-pulse text-chalk motion-reduce:animate-none">today</span>
        <span>{AGE_SCALE[AGE_SCALE.length - 1]!.label}</span>
      </div>
    </div>
  );
}
